'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Zap, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

interface SignUpFormValues {
  displayName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.05-3.71 1.05-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function getPasswordStrength(password: string): number {
  if (password.length < 6) return 0;
  let score = 1;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password) && /[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return Math.min(score, 3);
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t } = useLanguage();

  const passwordStrengthLevels = [
    { label: t.auth.tooShort, color: 'bg-danger', width: 'w-1/4' },
    { label: t.auth.weak, color: 'bg-warning', width: 'w-2/4' },
    { label: t.auth.good, color: 'bg-info', width: 'w-3/4' },
    { label: t.auth.strong, color: 'bg-positive', width: 'w-full' },
  ];

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignUpFormValues>({
    defaultValues: { displayName: '', email: '', password: '', confirmPassword: '', agreeToTerms: false },
  });

  const watchedPassword = watch('password', '');
  const passwordStrength = getPasswordStrength(watchedPassword);
  const strengthConfig = passwordStrengthLevels[passwordStrength];

  const onSubmit = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setSuccess(true);
      toast.success(t.auth.accountCreated);
    }, 1000);
  };

  if (success) {
    return (
      <div className="flex flex-col items-center text-center gap-5 py-6">
        <div className="w-16 h-16 rounded-2xl bg-positive/10 border border-positive/30 flex items-center justify-center">
          <CheckCircle size={32} className="text-positive" />
        </div>
        <div>
          <h2 className="text-xl font-extrabold text-foreground">{t.auth.youreIn}</h2>
          <p className="text-sm text-muted-foreground mt-2">{t.auth.accountReady}</p>
        </div>
        <a href="/" className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2">
          <Zap size={15} />
          {t.auth.goToCalculator}
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-2xl font-extrabold text-foreground">{t.auth.createYourAccount}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t.auth.freeForever}</p>
      </div>

      {/* Google Sign Up */}
      <button
        type="button"
        className="btn-ghost w-full py-3 text-sm flex items-center justify-center gap-2"
        onClick={() => toast.info(t.auth.googleComingSoon)}
      >
        <GoogleIcon />
        {t.auth.signUpWithGoogle}
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">{t.auth.orEmail}</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        {/* Display Name */}
        <div>
          <label htmlFor="signup-name" className="block text-sm font-semibold text-foreground mb-1.5">
            {t.auth.creatorName}
          </label>
          <p className="text-xs text-muted-foreground mb-2">{t.auth.creatorNameHint}</p>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder={t.auth.creatorNamePlaceholder}
            className="input-field w-full px-4 py-3 text-sm"
            {...register('displayName', {
              required: t.auth.creatorNameRequired,
              minLength: { value: 2, message: t.auth.creatorNameMin },
            })}
          />
          {errors.displayName && <p className="text-xs text-danger mt-1.5">{errors.displayName.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="signup-email" className="block text-sm font-semibold text-foreground mb-1.5">
            {t.auth.emailAddress}
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="input-field w-full px-4 py-3 text-sm"
            {...register('email', {
              required: t.auth.emailRequired,
              pattern: { value: /^\S+@\S+\.\S+$/, message: t.auth.emailInvalid },
            })}
          />
          {errors.email && <p className="text-xs text-danger mt-1.5">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-sm font-semibold text-foreground mb-1.5">
            {t.auth.password}
          </label>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Min. 6 characters"
              className="input-field w-full px-4 py-3 text-sm pr-11"
              {...register('password', {
                required: t.auth.passwordRequired,
                minLength: { value: 6, message: t.auth.passwordMin },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-danger mt-1.5">{errors.password.message}</p>}
          {watchedPassword.length > 0 && (
            <div className="mt-2">
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-300 ${strengthConfig.color} ${strengthConfig.width}`} />
              </div>
              <p className={`text-[11px] mt-1 font-semibold ${
                passwordStrength === 0 ? 'text-danger' :
                passwordStrength === 1 ? 'text-warning' :
                passwordStrength === 2 ? 'text-info' : 'text-positive'
              }`}>
                {strengthConfig.label}
              </p>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-semibold text-foreground mb-1.5">
            {t.auth.confirmPassword}
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repeat your password"
              className="input-field w-full px-4 py-3 text-sm pr-11"
              {...register('confirmPassword', {
                required: t.auth.confirmPasswordRequired,
                validate: (val) => val === watchedPassword || t.auth.passwordMismatch,
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirm ? t.auth.hidePassword : t.auth.showPassword}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-xs text-danger mt-1.5">{errors.confirmPassword.message}</p>}
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded accent-primary flex-shrink-0"
              {...register('agreeToTerms', { required: t.auth.agreeRequired })}
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {t.auth.agreeToTerms}{' '}
              <a href="#" className="text-primary hover:underline font-semibold">{t.auth.termsOfService}</a>{' '}
              {t.auth.and}{' '}
              <a href="#" className="text-primary hover:underline font-semibold">{t.auth.privacyPolicy}</a>
            </span>
          </label>
          {errors.agreeToTerms && <p className="text-xs text-danger mt-1.5">{errors.agreeToTerms.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <><Loader2 size={15} className="animate-spin" />{t.auth.creatingAccount}</>
          ) : (
            <><Zap size={15} />{t.auth.createAccountBtn}</>
          )}
        </button>
      </form>

      <p className="text-center text-xs text-muted-foreground">
        {t.auth.alreadyHaveAccount}{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">
          {t.auth.signInLink}
        </button>
      </p>
    </div>
  );
}